<template>
  <PAccordion v-model:activeIndex="activeIndex" class="swap-info-tab" lazy>
    <PAccordionTab>
      <template #header>
        <div class="swap-info-tab__header">
          <div>
            1&nbsp;{{ rateTokens.symbol.value }}&nbsp;=&nbsp;
            <Transition :duration="300" appear mode="out-in" name="text">
              <span :key="`rate-${swap.isUpdatingRate}`">
                {{ convertRateTokens.formatted.value }}
              </span></Transition
            >&nbsp; ({{ $t('common.includingFee') }})
          </div>
          <span>
            <i
              :class="[isToggled ? 'pi-chevron-up' : 'pi-chevron-down']"
              class="pi pi-chevron-down"
            />
          </span>
        </div>
      </template>
      <div class="list">
        <div class="list__item">
          <span>{{ $t('addLiquidity.expectedOutput') }}</span>
          <Transition :duration="300" appear mode="out-in" name="text">
            <span :key="`output-${outputTokens.formatted.value}`">{{
              outputTokens.formatted.value
            }}</span>
          </Transition>
        </div>
      </div>
    </PAccordionTab>
  </PAccordion>
  <div class="swap-info__info">
    <div class="list">
      <div class="list__item item">
        <div class="item__title">
          <span>{{ $t('transaction.priceImpact') }}</span>
          <span
            v-if="isShowHelp"
            v-tooltip.top="priceImpactTooltip"
            class="price-impact-tooltip"
          >
            <img alt="" height="24" src="@/assets/help-circle.svg" width="24" />
          </span>
        </div>
        <span :class="priceImpactClass" class="swap-info__price-impact-wrapper">
          <img alt="" class="mr-1" src="@/assets/alert-white.svg" />
          <Transition :duration="300" appear mode="out-in" name="text">
            <span :key="`price-impact-${isUpdatingRate}`">
              {{ priceImpactFormatted }}
            </span>
          </Transition>
          %
        </span>
      </div>
      <div class="list__item item">
        <div class="item__title">
          <span
            >{{ $t('header.fee') }}
            <template v-if="smartRouterMode === 'direct'">
              ({{ convertFee }}%)
            </template>
          </span>
          <span
            v-if="isShowHelp"
            v-tooltip.top="feeTooltip"
            class="fee-tooltip"
          >
            <img alt="" height="24" src="@/assets/help-circle.svg" width="24" />
          </span>
        </div>
        <span class="item__value">
          <Transition :duration="300" appear mode="out-in" name="text">
            <span v-if="feeUSDEquivalent" :key="`fee-${feeUSDEquivalent}`">
              ~{{ feeUSDEquivalent }}$
            </span>
          </Transition>
          <Transition :duration="300" appear mode="out-in" name="text">
            <span
              v-if="smartRouterMode === 'direct' && swap.convertFeeAmount"
              :key="swap.convertFeeAmount"
              class="primary"
            >
              {{ swap.convertFeeAmount }}
            </span>
          </Transition>
        </span>
      </div>
      <div v-if="hasSlippage" class="list__item item">
        <div class="item__title">
          <span
            >{{ isFrom ? 'Minimum received' : 'Maximum sent' }} ({{
              (swap.slippage ? swap.slippage * 100 : 0).toFixed(2)
            }}%)</span
          >
          <span
            v-if="isShowHelp"
            v-tooltip.top="slippageTooltip"
            class="fee-tooltip"
          >
            <img alt="" height="24" src="@/assets/help-circle.svg" width="24" />
          </span>
        </div>
        <Transition :duration="300" appear mode="out-in" name="text">
          <span
            :key="`slippage-${slippageOutputTokens.formatted.value}`"
            class="primary"
            >{{ slippageOutputTokens.formatted.value }}</span
          >
        </Transition>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useSwapStore } from '@/store';
import { computed, nextTick, ref, watch } from 'vue';
import { useCurrencyFormat } from '@/composables/useCurrencyFormat';
import { getTokenPrice, getUSDEquivalent } from '@/composables/useCoinPrice';
import { useI18n } from '@/composables/useI18n';
import { storeToRefs } from 'pinia';

defineOptions({
  inheritAttrs: false,
});

const { t } = useI18n();
const swap = useSwapStore();
const { setFeeInUsd, setIsUpdatingPriceImpact } = swap;
const {
  swapsFeeInEachPool,
  slippageAmount,
  convertRate,
  toCurrency,
  fromCurrency,
  priceImpactFormatted,
  priceImpactState,
  isUpdatingRate,
  smartRouterMode,
  convertFee,
  lastInteractiveField,
} = storeToRefs(swap);

const feeUSDEquivalent = ref<string | undefined>(undefined);
const isToggled = ref(false);

const hasSlippage = computed(
  () => slippageAmount.value && slippageAmount.value > 0,
);

const isShowHelp = ref(true);

const toAmount = computed(() => toCurrency.value.amount);
const toToken = computed(() => toCurrency.value.token);
const fromToken = computed(() => fromCurrency.value.token);

const isFrom = computed(() => lastInteractiveField.value === 'from');
const slippageToken = computed(() =>
  isFrom.value ? toToken.value : fromToken.value,
);

const rateTokens = useCurrencyFormat(1, fromToken);
const outputTokens = useCurrencyFormat(toAmount, toToken);
const slippageOutputTokens = useCurrencyFormat(slippageAmount, slippageToken);

const priceImpactClass = computed(() => {
  return priceImpactState.value === 'normal'
    ? 'swap-info__price-impact-wrapper_success'
    : priceImpactState.value === 'warning'
    ? 'swap-info__price-impact-wrapper_warning'
    : 'swap-info__price-impact-wrapper_alert';
});

const convertRateTokens = computed(() =>
  useCurrencyFormat(convertRate, toToken),
);

const priceImpactTooltip = computed(() => ({
  value: t('help.priceImpact'),
  class: 'price-impact-tooltip',
}));

const feeTooltip = computed(() => ({
  value: t('help.fee'),
  class: 'fee-tooltip',
}));

const slippageTooltip = computed(() => ({
  value: t('help.minimumReceived'),
  class: 'slippage-tooltip',
}));

const activeIndex = computed({
  get() {
    return isToggled.value ? 0 : undefined;
  },
  set(value?: number) {
    nextTick(() => {
      isToggled.value = value === 0;
    });
  },
});

watch(
  [() => swap.convertFeeAmount, swapsFeeInEachPool],
  async () => {
    const feeAmount =
      Array.isArray(swapsFeeInEachPool.value) &&
      swapsFeeInEachPool.value.length > 0
        ? swapsFeeInEachPool.value.map((token) => {
            return useCurrencyFormat(token.feeAmount, token.symbol, {
              useSuffix: false,
            });
          })
        : [];

    const prices = (await Promise.all(
      feeAmount?.map((token) => getTokenPrice(token?.symbol?.value || '')),
    )) as number[];

    if (!prices || !Array.isArray(prices) || prices.length === 0) return;

    const _feeUSDEquivalent = prices
      .reduce((acc, price, index) => {
        return (acc += getUSDEquivalent(
          +(feeAmount[index]?.formatted?.value || 0),
          price,
        ) as number);
      }, 0)
      .toFixed(3);

    feeUSDEquivalent.value = !Number.isNaN(+_feeUSDEquivalent)
      ? _feeUSDEquivalent
      : '0';

    setFeeInUsd(feeUSDEquivalent.value);
  },
  { immediate: true, deep: true },
);
</script>

<style lang="scss" scoped>
.swap-info {
  &__price-impact-wrapper {
    display: flex;
    align-items: center;
    font-weight: 500;
    font-size: 15px;
    line-height: 18px;
    padding: 8px;
    border-radius: 7px;
    color: white;
    min-width: 5.5rem;
    position: relative;
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.3px;
    justify-content: space-between;

    &_success {
      background-color: var(--accent-success);
    }

    &_warning {
      background-color: var(--accent-warning);
    }

    &_alert {
      background-color: var(--accent-error);
    }

    span {
      margin-left: auto;
    }
  }
}

.swap-info__info {
  margin: 8px 0 0;
  padding: 0.25rem 1.25rem 0;

  .list__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: var(--text-color-secondary);
    .primary {
      color: var(--primary-color-text);
    }
  }
}

.swap-info-tab {
  ::v-deep(.p-accordion-tab) {
    &.p-accordion-tab-active {
      .p-accordion-header-link {
        background: #24243a !important;
      }
    }

    .p-accordion-header-link {
      width: 100%;
      padding: 4px 12px !important;
      background: transparent !important;
      border: 0;
      border-radius: 12px !important;
      min-height: 40px;
      box-shadow: none !important;

      &:focus {
        box-shadow: none !important;
      }

      .p-accordion-toggle-icon {
        display: none;
      }
    }

    .p-accordion-content {
      margin-top: 8px;
      background: transparent !important;
      border-radius: 16px !important;
      border: 1px solid #24243a !important;
    }
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;

    div {
      padding: 0 5px;
      display: flex;
      align-items: center;
      font-size: 1rem;
      font-weight: 500 !important;
      span {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 2.8rem;
      }
    }
  }
}

.list {
  display: flex;
  flex-direction: column;
  row-gap: 0.5rem;

  &__item {
    display: flex;
    font-weight: 400;
    font-size: 15px;
    min-height: 2.125rem;
    align-items: center;
    justify-content: space-between;
    min-width: 420px;
    width: 100%;
    position: relative;
    @media screen and (max-width: 768px) {
      min-width: 100%;
    }
  }
}

.item {
  &__title {
    display: flex;
    align-items: center;
    gap: 8px;
    span:last-child {
      display: flex;
    }
  }
  &__value {
    position: relative;
    overflow: hidden;
    margin-left: auto;
  }
}

.-white {
  color: white !important;
}

.price-impact-tooltip {
  ::v-deep(.p-tooltip-text) {
    max-width: 16rem;
  }
}

.text-enter-from,
.text-leave-to {
  opacity: 0.7;
  filter: grayscale(1);
}

.text-enter-to,
.text-leave-from {
  opacity: 1;
}

.text-enter-active,
.text-leave-active,
.text-move {
  transition: all 0.5s ease-out;
}
</style>

<style lang="scss">
.tooltip-version-pools {
  .p-tooltip-text {
    width: 16rem;
  }
}
</style>
